import React, { useEffect, useState } from 'react'
import { cn } from '@/utils/style.ts'
import { ClassValue } from 'clsx'
import { FormSectionProvider } from '@/common/components/Form/useFormSection.tsx'
import { last, startCase } from 'lodash-es'
import { SwitchInput } from '@/common/components/Form/FormSwitch.tsx'
import { useFormContext, useWatch } from 'react-hook-form'

type FormSectionProps = {
  name?: string
  label?: string
  children: React.ReactNode
  className?: ClassValue
  layoutClassName?: ClassValue
  asChild?: boolean
  optional?: boolean
}

const nameToLabel = (name: string) => startCase(last(name.split('.')))

export const FormSectionTitle: React.FC<{ title: string }> = ({ title }) => {
  return <h4 className="text-lg font-semibold mb-4 inline-block">{title}</h4>
}

export const FormSection: React.FC<FormSectionProps> = ({
  name,
  label,
  className,
  layoutClassName,
  children,
  optional = false,
  asChild = false,
}) => {
  const finalLabel = !label && name ? nameToLabel(name) : label

  if (asChild) {
    return (
      <FormSectionProvider name={name}>
        {children}
      </FormSectionProvider>
    )
  }

  return (
    <FormSectionProvider name={name}>
      <div className={cn('border-b pb-4 relative', className)}>
        {finalLabel && <FormSectionTitle title={finalLabel} />}
        {optional && <SectionSwitch name={name || ''} />}
        <div
          className={cn('relative', {
            'w-full grid grid-cols-4 gap-4': !layoutClassName,
          }, layoutClassName)}
        >
          {children}
        </div>
      </div>
    </FormSectionProvider>
  )
}

type SectionSwitchProps = {
  name: string
}

const SectionSwitch: React.FC<SectionSwitchProps> = ({ name }) => {
  const { setValue, trigger, getValues } = useFormContext()
  const [checked, setChecked] = useState<boolean>(() => {
    return !!getValues(name)
  })

  const value = useWatch({ name })

  useEffect(() => {
    if (value && !checked) {
      setChecked(true)
    } else if (!value && checked) {
      setChecked(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, value]) // don't add checked to dependency array

  useEffect(() => {
    if (!checked) {
      setValue(name, null)
      trigger(name)
    } else {
      const oldValue = getValues(name)
      if (!oldValue) {
        setValue(name, {})
      }
    }
  }, [checked, getValues, name, setValue, trigger])

  if (!name) {
    return null
  }

  return (
    <SwitchInput className="ml-4" value={checked} onChange={setChecked} />
  )
}